/** @format */
import { isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ComponentFactory,
	ComponentRef,
	Inject,
	Injector,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	TemplateRef,
	Type,
	ViewChild,
	ViewContainerRef,
	ɵcreateInjector as createInjector
} from '@angular/core';
import { Title } from '@angular/platform-browser';

import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';

import { chain } from '@core/chain';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import {
	CertonaRecommendations,
	CertonaScheme,
	CertonaService
} from '@product-recommendations/data-access';

import { HomrDataService, HomrPage } from '@shared/homr/data-access';
import {
	CookieService,
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	Scene7Service,
	UtilityService,
	WINDOW
} from '@shared/utils/services';

import { MetaService, Page, SeoService } from '@seo/data-access';

import { AuthenticationService } from '@shared/authentication/data-access';

import { Store as NgRxStore } from '@ngrx/store';
import { setHomrGTGTContent, setHomrScheduleContent } from '@state/data-access';
import { DynamicProductTemplateModules } from '@pdp/data-access/enums';
import {
	AppProductReviewState,
	ProductReviewsProsCons,
	ReviewService,
	setProductReviewProsCons
} from '@product-reviews/data-access';
import {
	BundleData,
	HomrBundleSpot,
	HomrGTGTContent,
	HomrScheduleContent,
	IHomrRichContent,
	IHomrSpot,
	ImportedModule,
	ISkuLocation,
	KillSwitches,
	ModernPromotionResponse,
	MyCustomizerMap,
	ProcessedNonBundleProduct,
	ProcessedProduct,
	ProductsData,
	ProductSku,
	ProductStyle,
	ProductStyleSeo,
	PromotionDetailsMapV1,
	PromotionDetailsMapV2,
	QueryParam
} from '@shared/data-access/interfaces';
import {
	DiscountEligibilityService,
	HttpService,
	LoyaltyService,
	PointsService,
	ProductService,
	Store
} from '@shared/data-access/services';
import { Logger } from '@logger';
import {
	selectAllProductStyle,
	selectEcommInventory,
	selectKillswitch,
	setPromotionData
} from '@pdp/data-access/state';
import {
	CustomProductService,
	ProductSelectionService,
	QueryStringParamService
} from '@pdp/data-access/services';
import {
	DynamicProductTemplateComponent,
	DynamicProductTemplateModule
} from '@pdp/templates/shared/dynamic-product-template-module.interface';

const expectedAdditionalQueryParams = [
	'action',
	'caliber-gauge',
	'framesize',
	'glovehand',
	'hand',
	'handsize',
	'inseam',
	'length',
	'pieces',
	'power',
	'rodlength',
	'shoesize',
	'size',
	'skatesize',
	'socksize',
	'throwinghand'
];

const actualAdditionalQueryParams: QueryParam[] = [];

@Component({
	selector: 'pdp-product-container',
	templateUrl: './product-container.component.html',
	styleUrl: './product-container.component.scss',
	standalone: false
})
export class ProductContainerComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('aboveTheFoldTemplate') aboveTheFoldTemplate: TemplateRef<void>;
	@ViewChild('aboveTheFoldViewRef', { read: ViewContainerRef })
	aboveTheFoldViewRef: ViewContainerRef;

	/**
	 * @description
	 * Response for Certona Recommendations
	 */
	certonaRecommendationsResponse: CertonaRecommendations;

	/**
	 * @description
	 * The channel ID based on user-agent
	 */
	channelId = this.deviceService.getDeviceChannelId();

	/**
	 * @description
	 * The default color attribute to display the initial product image(s)
	 */
	defaultColor = '';

	/**
	 * @description
	 * Check If the certona response have scheme for cart
	 */
	isCartCertona = false;

	/**
	 * @description
	 * Check If the certona response have scheme for PLA
	 */
	isResonanceReady = false;

	/**
	 * @description
	 * The Page object which holds SEO data
	 */
	page: Page;

	/**
	 * @description
	 * The current Product; Used to pass into the child components
	 */
	product: ProcessedProduct | Record<string, never>;

	/**
	 * @description
	 * Product promotions map
	 */
	promotionsMap: PromotionDetailsMapV1 | PromotionDetailsMapV2;

	/**
	 * @description
	 * Color Query Param set from the url
	 */
	colorQueryParam: string;

	/**
	 * @description
	 * Product design Id
	 */
	designIdParam?: string;

	/**
	 * @description
	 * Template outlet for dynamically loaded templates
	 */
	@ViewChild('templateOutlet', { read: ViewContainerRef, static: false })
	templateOutlet: ViewContainerRef;

	/**
	 * @description
	 * bopis location inventory
	 */
	private bopisLocationInventoryMap: ISkuLocation[];

	/**
	 * @description
	 * the dynamic template component reference after it has been loaded
	 */
	private dynamicComponentRef: ComponentRef<DynamicProductTemplateComponent>;

	/**
	 * @description
	 * Default dynamic template to load. Depending on product availability and / or hierarchy, this will
	 * be updated to load the appropriate template
	 */
	public dynamicTemplateToLoad = DynamicProductTemplateModules.IN_STOCK_DEFAULT;

	/**
	 * @description
	 * The Kill Switches for the environment
	 */
	public killSwitches: KillSwitches;

	/**
	 * @description
	 * Ecomm inventory map for product sku id list
	 */
	private shipToMeInventoryMap: ISkuLocation[];

	/**
	 * @description
	 * Subject used for unsubscribing
	 */
	private destroy$ = new Subject<void>();

	/**
	 * @description
	 * sku id passed from the url ?sku="skuId"
	 */
	skuIdQueryParam: string | undefined;

	// specifically for QV switch
	isLoaded: boolean;

	favoritesEnabled = false;

	isHighlyRatedReviewsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	/**
	 * @description
	 * replaces the isPlatformBrowser(this.platformId) function call
	 */
	isPlatformBrowser = false;

	/**
	 * @description Feature flag to enable above the fold mobile container.
	 */
	useAboveTheFoldMobileContainer = false;

	/**
	 * @description Customizer map
	 */
	myCustomizerMap: MyCustomizerMap;
	myCustomizerMap$: Observable<MyCustomizerMap>;

	chainIdentifierAbbr = chain.chainIdentifierAbbr;

	isSSRPLAEnabled = false;
	isPLAEnabled = false;
	isPLADSCarouselEnabled = false;
	isPLA = false;

	// eslint-disable-next-line max-params
	constructor(
		@Inject(PLATFORM_ID) protected platformId: string,
		@Inject(WINDOW) private window: Window,
		private authService: AuthenticationService,
		private changeRef: ChangeDetectorRef,
		private cookieService: CookieService,
		private deviceService: DeviceService,
		private eventBusService: EventBusService,
		private homrDataService: HomrDataService,
		private httpService: HttpService,
		private injector: Injector,
		private loyaltyService: LoyaltyService,
		private metaService: MetaService,
		private ngRxstore: NgRxStore,
		private pointsService: PointsService,
		private productContainerService: CertonaService,
		private productSelectionService: ProductSelectionService,
		private productService: ProductService,
		private prosConsStore: NgRxStore<AppProductReviewState>,
		private reviewService: ReviewService,
		private scene7Service: Scene7Service,
		private seoService: SeoService,
		private store: Store,
		private title: Title,
		private utilityService: UtilityService,
		private discountEligibilityService: DiscountEligibilityService,
		private customProductService: CustomProductService,
		public logger: Logger
	) {}

	ngOnInit() {
		// Assign function's value only once
		this.isPlatformBrowser = isPlatformBrowser(this.platformId);

		// Subscribe to the environment kill switch values
		this.store
			.select('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(
				filter((killSwitches) => killSwitches !== undefined && killSwitches !== null),
				take(1)
			)
			.subscribe({
				next: (killSwitches: KillSwitches) => {
					// Set the kill switches from the response
					this.killSwitches = killSwitches;
					this.favoritesEnabled = this.utilityService.checkKillSwitch(
						'favoritesEnabled',
						this.killSwitches,
						chain.chainIdentifierAbbr
					);
					this.useAboveTheFoldMobileContainer = this.utilityService.checkKillSwitch(
						'useAboveTheFoldMobileContainer',
						this.killSwitches,
						chain.chainIdentifierAbbr
					);
					this.isSSRPLAEnabled = this.utilityService.checkKillSwitch(
						'isSSRPLAEnabled',
						this.killSwitches,
						chain.chainIdentifierAbbr
					);
					this.isPLAEnabled = this.utilityService.checkKillSwitch(
						'isPLAEnabled',
						this.killSwitches,
						chain.chainIdentifierAbbr
					);
					this.isPLADSCarouselEnabled = this.utilityService.checkKillSwitch(
						'isPLADSCarouselEnabled',
						this.killSwitches,
						chain.chainIdentifierAbbr
					);
				},
				error: (error) => {
					// Set the kill switches to undefined
					this.killSwitches = undefined;

					// Throw the error
					throw new Error(error.message);
				}
			})
			.add(() => {
				// Trigger the KillSwitchDataComplete Event
				this.eventBusService.emit(
					new EmitEvent(Events.KillSwitchDataComplete, this.killSwitches)
				);

				if (this.isPLAEnabled && this.isSSRPLAEnabled && !this.isPLADSCarouselEnabled) {
					this.isPLA = this.productService.isPLA(this.isPlatformBrowser);
				}
			});

		this.isLoaded = true;

		// Quick View Product from Store
		this.ngRxstore
			.select(selectAllProductStyle)
			.pipe(take(1))
			.subscribe((productData: Readonly<ProductStyle | Record<string, never>>) => {
				// Check for Bundle Product
				const bundleCheckResult = this.checkForBundleProduct(productData);
				const { tempProduct } = bundleCheckResult;
				const isBundle =
					'isBundle' in bundleCheckResult ? bundleCheckResult.isBundle : null;
				const ecodes = 'isBundle' in bundleCheckResult ? bundleCheckResult.ecodes : null;

				// Check if product has >1 key. If not, go to 404
				if (this.isProductFound<BundleData | ProductStyle>(tempProduct)) {
					// Get skus data
					const skus = this.productSelectionService.getProductSkusData();
					this.setDynamicTemplateToLoad(isBundle, skus);

					// Get the query string if present
					const queryString = this.httpService.getQueryString();
					/*
					 * Query Param logic
					 */
					this.queryParamValidations(queryString, skus);

					/*
					 * Defining Attributes logic/iteration
					 */
					this.checkForDefiningAttributes(tempProduct);

					this.product = {
						...tempProduct,
						skus,
						...(isBundle && {
							isBundle,
							ecodes
						}),
						defaultColor: this.defaultColor,
						colorQueryParam: this.colorQueryParam,
						skuIdQueryParam: this.skuIdQueryParam,
						actualAdditionalQueryParams,
						skuIdList: skus
							? this.productSelectionService.generateSkuIdListArray()
							: undefined
					};

					/*
					 * SEO
					 */
					// Set the Page SEO properties

					this.setProductPageSEOData(
						this.product.partNumber,
						this.product.name,
						this.product.seo
					);

					/*
					 * Custom Products QV
					 */
					this.customProductQV();

					if (this.useAboveTheFoldMobileContainer) {
						this.initializeAboveTheFoldData();
					}

					// Load the dynamic template
					this.loadDynamicModule$(this.dynamicTemplateToLoad)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: () => {
								// Load data that requires the template to exist so it can set component properties
								this.loadTemplatePostLoadData(
									this.product as ProcessedProduct,
									this.channelId
								);
							},
							error: (error) => {
								// Throw an error so we can log it
								throw new Error(error);
							},
							complete: () => this.emitEnableFooterEvent()
						});

					/*
					 * Certona
					 */
					// Check to see if a Certona callback has been defined already
					this.checkForCertonaRecommendations();
				} else {
					/*
					 * Product Not Found (404)
					 */
					this.product = tempProduct;

					// Update the template to load 404
					this.dynamicTemplateToLoad = DynamicProductTemplateModules.NOT_FOUND_DEFAULT;

					// Load the dynamic template
					this.loadDynamicModule$(this.dynamicTemplateToLoad)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: (templateData) => {
								console.log(`DID TEMPLATE LOAD? ${templateData.didTemplateLoad}`);
								console.log(`TEMPLATE NAME? ${templateData.templateName}`);
							},
							error: (error) => {
								// Throw an error so we can log it
								throw new Error(error);
							},
							complete: () => this.emitEnableFooterEvent()
						});
				}
			});
	}

	private isProductFound = <T = ProcessedProduct>(
		product: T | Record<string, never>
	): product is T => Object.keys(product).length > 1;

	private customProductQV(): void {
		// Check to see if there are any parsed descriptive attributes
		if (
			'descriptiveAttributes' in this.product &&
			typeof this.product.descriptiveAttributes !== 'undefined'
		) {
			if (this.product.skus && this.product.skus.length > 0 && this.product.isCustomizable) {
				// Update the template to load custom
				this.dynamicTemplateToLoad = DynamicProductTemplateModules.CUSTOM_DEFAULT;
			}

			// Check to see if the product has a customizable version as well
			const customizableEcode = this.product.descriptiveAttributes.find(
				(attr) => attr.identifier === '5424'
			);
			if (
				customizableEcode &&
				typeof customizableEcode.value === 'string' &&
				customizableEcode.value.trim() !== ''
			) {
				// use the Product Details API v4/byPartNumber endpoint
				this.productService
					.getProductByEcodeV4$(customizableEcode.value)
					.subscribe((customProduct) => {
						if (customProduct) {
							(this.product as ProcessedNonBundleProduct).customSeoUrl = `${
								customProduct.style.pdpSeoUrl
							}?stockReferral=true`;
						}
					});
			}
		}
	}

	private checkForDefiningAttributes(product: ProductStyle | BundleData): void {
		// Check to see if there are any defining attributes
		if (!('definingAttributes' in product) || !Array.isArray(product['definingAttributes'])) {
			return;
		}

		// Iterate the defining attributes
		product['definingAttributes']
			// Check for the color attribute
			.filter(
				(definingAttribute) =>
					typeof definingAttribute.name === 'string' &&
					definingAttribute.name.trim().toLowerCase() === 'color'
			)
			.forEach((attribute) => {
				// Check to see if any colors are available
				if (!attribute.value) {
					this.defaultColor = '';
					return;
				}

				// Create array of color values for checking query string color value
				const colorArray = attribute.value
					.split('|')
					.map((value) => value.replace(/[/ -]/g, '_'));

				// Check to see if only a single color is available
				this.defaultColor =
					this.defaultColor && colorArray.includes(this.defaultColor)
						? this.defaultColor
						: this.productService.getDefaultColorFromDefiningAttribute(attribute);
			});
	}

	private queryParamValidations(queryString: string, skus: ProductSku[]): void {
		// Check to see if the color param is present
		if (QueryStringParamService.doesQueryStringHaveParameter(queryString, 'color')) {
			// Set the default color attribute from the PLP
			this.defaultColor = this.setColorQueryParamValue(queryString);
			this.colorQueryParam = this.setColorQueryParamValue(queryString);
		}

		if (QueryStringParamService.doesQueryStringHaveParameter(queryString, 'sku')) {
			// set the sku id passed from the param
			this.skuIdQueryParam = this.httpService.getParameterValuesFromQueryString(
				queryString,
				'sku'
			);

			const defaultColorFromSku = this.productService.getColorForSkuId(
				skus,
				this.skuIdQueryParam
			);

			if (defaultColorFromSku) {
				this.defaultColor = defaultColorFromSku.replace(/[/ -]/g, '_');
			}
		}

		if (
			this.useAboveTheFoldMobileContainer &&
			QueryStringParamService.doesQueryStringHaveParameter(queryString, 'designId')
		) {
			// get the design id passed from the param
			this.designIdParam = this.httpService.getParameterValuesFromQueryString(
				queryString,
				'designId'
			);
		}

		// Check additional query params for Attribute Selection
		expectedAdditionalQueryParams.forEach((param) => {
			// Check to see if the param is present
			if (QueryStringParamService.doesQueryStringHaveParameter(queryString, param)) {
				actualAdditionalQueryParams.push({
					name: param,
					value: this.httpService.getParameterValuesFromQueryString(queryString, param)
				});
			}
		});
	}

	private setDynamicTemplateToLoad(isBundle: boolean, skus: ProductSku[]) {
		if (isBundle) {
			// Bundle template
			this.dynamicTemplateToLoad = DynamicProductTemplateModules.BUNDLE_DEFAULT;
		} else if (!skus || skus?.length === 0) {
			// load OOS template if no skus are available in product
			this.dynamicTemplateToLoad = DynamicProductTemplateModules.OUT_OF_STOCK_DEFAULT;
		} else {
			// set default template if skus are avaiable
			this.dynamicTemplateToLoad = DynamicProductTemplateModules.IN_STOCK_DEFAULT;
		}
	}

	private checkForBundleProduct(productData: ProductStyle | Record<string, never>):
		| {
				tempProduct: ProductStyle | Record<string, never> | null;
		  }
		| {
				ecodes: ProductsData[] | null;
				isBundle: true;
				tempProduct: BundleData;
		  } {
		// Check for Bundle Product
		if (Object.keys(productData).length === 0) {
			const tempProduct: BundleData | Record<string, never> = {
				...this.productSelectionService.getBundleData()
			};
			if (this.isProductFound<BundleData>(tempProduct)) {
				return {
					tempProduct,
					ecodes: this.productSelectionService.getBundleProductsData(),
					isBundle: true
				};
			}
			return {
				tempProduct
			};
		}
		return {
			tempProduct: { ...productData }
		};
	}

	private checkForCertonaRecommendations(): void {
		if (typeof this.window.certonaRecommendations === 'undefined') {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.certonaRecommendations = this.getCertonaRecommendations.bind(this);
		}

		// Check to see if a Certona callback has been defined already
		if (
			typeof this.window.DSGShoppingPathJS === 'undefined' ||
			typeof this.window.DSGShoppingPathJS.certonaRecommendations === 'undefined'
		) {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.DSGShoppingPathJS = {
				certonaRecommendations: this.getCertonaRecommendations.bind(this)
			};
		}
	}

	ngAfterViewInit(): void {
		if (this.useAboveTheFoldMobileContainer) {
			this.aboveTheFoldViewRef.createEmbeddedView(this.aboveTheFoldTemplate);
		}

		this.utilityService.setupQuestionsAndAnswersAIWidget(this.product.partNumber);
	}

	initializeAboveTheFoldData() {
		if (
			this.dynamicTemplateToLoad === DynamicProductTemplateModules.CUSTOM_DEFAULT &&
			this.designIdParam
		) {
			this.myCustomizerMap$ = this.customProductService.getCustomizedProductAttributes$(
				this.designIdParam
			);
			this.myCustomizerMap$.subscribe((res: MyCustomizerMap) => {
				if (res) {
					this.myCustomizerMap = res;
				}
			});
		} else {
			this.myCustomizerMap$ = of(undefined);
		}
	}

	setColorQueryParamValue(queryString: string) {
		return this.httpService
			.getParameterValuesFromQueryString(queryString, 'color')
			.replace(/[/ -]/g, '_')
			.replace(/~2F/g, '_');
	}

	private getUniqueCertonaSchemes(mergedCertonaSchemes: CertonaScheme[]) {
		const uniqueCertonaSchemes = [];

		const uniqueSchemes = new Set();

		const handleSpecificScheme = (item: CertonaScheme) => {
			const existingIndex = uniqueCertonaSchemes.findIndex(
				(eachCertonaScheme) => eachCertonaScheme.scheme === item.scheme
			);
			uniqueCertonaSchemes.splice(existingIndex, 1);
			uniqueCertonaSchemes.push({ ...item });
		};

		// Iterate the merged schemes
		for (const item of mergedCertonaSchemes) {
			if (!uniqueSchemes.has(item.scheme)) {
				uniqueSchemes.add(item.scheme);
				uniqueCertonaSchemes.push({ ...item });
			} else if (item.scheme === 'skuoos_rr' || item.scheme === 'skuoos2_rr') {
				// needed to update sku level certona response
				handleSpecificScheme(item);
			}
		}

		return uniqueCertonaSchemes;
	}

	/**
	 * @description
	 * Callback function for Certona to process the product recommendations.  It sets the response on the component to be passed
	 * into the child components
	 *
	 * @param certonaResponse
	 */
	getCertonaRecommendations(certonaResponse: CertonaRecommendations) {
		// Check to see if there is a response from Certona
		if (certonaResponse) {
			if (!this.certonaRecommendationsResponse) {
				// Set the response on the component
				this.certonaRecommendationsResponse = certonaResponse;
			} else if (
				this.certonaRecommendationsResponse.resonance?.schemes &&
				certonaResponse.resonance?.schemes
			) {
				// Merge the two
				const mergedCertonaSchemes = [
					...this.certonaRecommendationsResponse.resonance.schemes,
					...certonaResponse.resonance.schemes
				];

				// Unique schemes
				const uniqueCertonaSchemes = this.getUniqueCertonaSchemes(mergedCertonaSchemes);

				// Set the response on the component by merging the new with the existing
				this.certonaRecommendationsResponse = {
					resonance: {
						schemes: uniqueCertonaSchemes
					},
					source: certonaResponse.source
				};
			}
		}
		// Set the isPLAEnabled flag to true when pdplanding_rr is present in response.
		for (const schema of this.certonaRecommendationsResponse.resonance.schemes) {
			if (schema.scheme === 'pdplanding_rr') {
				this.isResonanceReady = true;
				this.productContainerService.resonanceReady(this.isResonanceReady);
			}
			if (schema.scheme === 'cartmodal_rr' || schema.scheme === 'cartmodal1_rr') {
				this.isCartCertona = true;
				this.productContainerService.cartCertona(this.isCartCertona);
			}
			// Map Certona landing page scheme names to non-landing-page scheme names.
			// Monetate does not use the landing page specific scheme names anymore.
			const schemeMap = new Map([
				['productlanding_rr', 'product_rr'],
				['product1landing_rr', 'product1_rr'],
				['product2landing_rr', 'product2_rr']
			]);
			if (schemeMap.has(schema.scheme)) {
				schema.scheme = schemeMap.get(schema.scheme);
			}
		}
		this.changeRef.detectChanges();
		this.productContainerService.recommendations(this.certonaRecommendationsResponse);
	}

	/**
	 * @description
	 * Emits the event to enable the Feedback and Footer
	 */
	private emitEnableFooterEvent(): void {
		this.eventBusService.emit(new EmitEvent(Events.EnableFooter));
	}

	/**
	 * @description
	 * Gets homR data and populates the necessary inputs on the rendered template
	 */
	private getAndSetHomrData$() {
		// homR page ID for messaging
		const homrPageName: string =
			'id' in this.product && this.product.id
				? (this.product.id as string)
				: this.product.partNumber;

		// homr page ID for in store services call
		let homrInStoreServicesPageName = null;
		// @ts-expect-error Should not ever get hit but leaving just in case
		if (this.product.id && this.product.descAttributesParsed.inStoreServices) {
			// @ts-expect-error See above
			homrInStoreServicesPageName = this.product.descAttributesParsed.inStoreServices;
		}

		// Gettting the InStore Service Attr
		if (this.product.partNumber && 'descriptiveAttributes' in this.product) {
			const inStoreServiceAttr = this.product.descriptiveAttributes?.find(
				(attr) => attr.identifier === '5439'
			);
			if (inStoreServiceAttr) {
				homrInStoreServicesPageName = inStoreServiceAttr.value;
			}
		}

		/*
		 * Standard PDP homR template data
		 */
		this.homrDataService
			.getData$('pdp', 2, homrPageName)
			.subscribe((homrTemplate: HomrPage) => {
				// Check to ensure there is a component ref
				if (typeof this.dynamicComponentRef !== 'undefined') {
					// Check to see if homR template has data
					if (homrTemplate?.content && Object.keys(homrTemplate.content).length !== 0) {
						const homrContent = [];

						if (
							Array.isArray(homrTemplate.content.freeShipMessage) &&
							homrTemplate.content.freeShipMessage.length > 0
						) {
							// populate the freeShipMessage homR spot
							const homrSpot: IHomrSpot = {
								contentName: 'freeShipMessage',
								message: homrTemplate.content.freeShipMessage[0].message,
								link: homrTemplate.content.freeShipMessage[0].link,
								border: homrTemplate.content.freeShipMessage[0].border
							};

							homrContent.push(homrSpot);
						}

						if (
							Array.isArray(homrTemplate.content.pdpBundlePromo) &&
							homrTemplate.content.pdpBundlePromo.length > 0
						) {
							// populate pdpBundlePromo homr spot
							const homrSpot: HomrBundleSpot = {
								contentName: 'pdpBundlePromo',
								promoShortDescription:
									homrTemplate.content.pdpBundlePromo[0].promoShortDescription,
								promoPercentOff:
									homrTemplate.content.pdpBundlePromo[0].promoPercentOff,
								promoBundlePrice:
									homrTemplate.content.pdpBundlePromo[0].promoBundlePrice
							};

							this.dynamicComponentRef.instance['homrBundleContent'] = homrSpot;
							this.store.set('HOMR_DATA', { homrBundleContent: homrSpot });
						}

						// Set the content in observable store
						this.store.set('HOMR_DATA', { homrContent });
					}

					const homrContentKeyCountdownTimer = 'Countdown Timer';

					// Check to see if homr template has data
					if (
						homrTemplate?.content &&
						homrTemplate?.contentScheduleMeta !== undefined &&
						homrTemplate.contentScheduleMeta[homrContentKeyCountdownTimer] !== undefined
					) {
						// populate the schedule from the contentScheduleMeta type
						const homrScheduleContent: HomrScheduleContent = {
							startDate:
								homrTemplate.contentScheduleMeta[homrContentKeyCountdownTimer][
									'startDate'
								],
							endDate:
								homrTemplate.contentScheduleMeta[homrContentKeyCountdownTimer][
									'endDate'
								],
							contentType:
								homrTemplate.contentScheduleMeta[homrContentKeyCountdownTimer][
									'contentType'
								]
						};

						// Set the input
						this.dynamicComponentRef.instance['homrScheduleContent'] =
							homrScheduleContent;
						this.store.set('HOMR_DATA', { homrScheduleContent });
					}

					// Start change detection
					this.dynamicComponentRef.changeDetectorRef.detectChanges();
				}
			});

		/*
		 * PDP homR rich content template data
		 */
		this.homrDataService
			.getData$('product-description', 2, homrPageName, true)
			.subscribe((homrTemplate: HomrPage) => {
				// Check to ensure there is a component reference and to see if rich content template has data
				if (
					typeof this.dynamicComponentRef !== 'undefined' &&
					homrTemplate?.content &&
					Object.keys(homrTemplate.content).length !== 0
				) {
					// Get a reference to the homR content
					const content = homrTemplate.content;

					// homrRichContent will be null if template is neither A or B
					let homrRichContent: IHomrRichContent;

					// Template A
					if (homrTemplate.templateTag === 'tempA') {
						homrRichContent = {
							templateA: {
								categoriesHeader: {
									header: content.categoriesHeaderOptional?.[0]?.header,
									description: content.categoriesHeaderOptional?.[0]?.description
								},
								techIconsHeader: {
									header: content.trendingHeaderOptional?.[0]?.header,
									description: content.trendingHeaderOptional?.[0]?.description
								},
								techIcons: content.iconSection,
								halfImageHalfTextArray: content.halfImageHalfText,
								trendingArray: content.trending3,
								pdp3DImagery: content.pdp3DImagery
							}
						};
					} else if (homrTemplate.templateTag === 'tempB') {
						// Template B
						homrRichContent = {
							templateB: {
								categoriesHeader: {
									header: content.categoriesHeaderOptional?.[0]?.header,
									description: content.categoriesHeaderOptional?.[0]?.description
								},
								techIconsHeader: {
									header: content.trendingHeaderOptional?.[0]?.header,
									description: content.trendingHeaderOptional?.[0]?.description
								},
								techIcons: content.iconSection,
								fullStory: content.fullStoryV3?.[0],
								trendingArray: content.trending3,
								pdp3DImagery: content.pdp3DImagery
							}
						};
					} else if (homrTemplate.templateTag === 'tempC') {
						homrRichContent = homrTemplate.content;
					} else if (homrTemplate.content.productStatsSlider) {
						homrRichContent = homrTemplate.content;
					}

					// Set the content in observable store
					this.store.set('HOMR_DATA', { homrRichContent });
				}
			});

		/*
		 * PDP GTGT homR template data
		 */
		let hasShippableInventory = false;

		for (let i = 0, length = this.shipToMeInventoryMap?.length || 0; i < length; i++) {
			// Get the ATS Quantity
			const atsQty = this.utilityService.convertToNumber(this.shipToMeInventoryMap[i].atsqty);

			// Check to see if there is any inventory
			if (atsQty > 0) {
				// Flip the flag
				hasShippableInventory = true;

				break;
			}
		}

		// Check to see if GTGT is enabled for the product and if it has shippable inventory
		if (this.product.gtgtenabled && hasShippableInventory) {
			this.homrDataService
				.getData$('gtgt', 2, homrPageName)
				.subscribe((homrTemplate: HomrPage) => {
					// Check to ensure there is a component ref and to see if homR template has data
					if (typeof this.dynamicComponentRef !== 'undefined' && homrTemplate?.content) {
						const gtgtContentKey = 'GTGT';

						const gtgtContentArray = homrTemplate.content[gtgtContentKey];

						if (Array.isArray(gtgtContentArray) && gtgtContentArray.length > 0) {
							const gtgtContent = gtgtContentArray[0];

							const homrGTGTContent: HomrGTGTContent = {
								headline: gtgtContent.title,
								link: gtgtContent.link,
								linkTitle: gtgtContent.linkTitle,
								Subhead: gtgtContent.Subhead,
								image: gtgtContent.image,
								altText: gtgtContent.altText,
								foregroundColor: gtgtContent.foregroundColor
							};

							this.ngRxstore.dispatch(setHomrGTGTContent({ homrGTGTContent }));

							// Check to see if the content schedule exists
							if (homrTemplate.contentScheduleMeta !== undefined) {
								this.ngRxstore.dispatch(
									setHomrScheduleContent({
										homrScheduleContent:
											this.getGTGTHomrScheduleContent(homrTemplate)
									})
								);
							}

							// Start change detection
							this.dynamicComponentRef.changeDetectorRef.detectChanges();
						}
					}
				});
		}
		/*
		 * PDP homR banner template data
		 */
		this.homrDataService
			.getData$('banner', 2, homrPageName, true)
			.subscribe((homrTemplate: HomrPage) => {
				if (
					Array.isArray(homrTemplate?.content?.imageBanner) &&
					homrTemplate.content.imageBanner.length > 0
				) {
					// populate the banner homR spot
					this.store.set('HOMR_DATA', {
						homrBanner: homrTemplate.content.imageBanner[0]
					});
				}
			});

		/**
		 * PDP homR in store services data
		 */
		if (homrInStoreServicesPageName) {
			// transform the page name
			homrInStoreServicesPageName = homrInStoreServicesPageName
				.toLowerCase()
				.replace(/\s/g, '-');
			this.homrDataService
				.getData$('pdp-services', 2, homrInStoreServicesPageName, true)
				.subscribe((homrTemplate: HomrPage) => {
					if (
						Array.isArray(homrTemplate?.content?.pdpServices) &&
						homrTemplate.content.pdpServices.length > 0
					) {
						// populate the in store services homR spot
						this.store.set('HOMR_DATA', {
							homrInStoreServices: homrTemplate.content.pdpServices[0]
						});
					}
				});
		}
	}

	private getGTGTHomrScheduleContent(homrTemplate: HomrPage) {
		const gtgtContent = homrTemplate.content['GTGT'][0];
		const gtgtContentScheduleMeta = homrTemplate.contentScheduleMeta['GTGT'];

		const arrivalTime = gtgtContent?.arrivalTime;
		const arrivalDate = gtgtContent?.arrivalDate;

		// populate the schedule from the contentScheduleMeta type
		const homrScheduleContent: HomrScheduleContent = {
			startDate: gtgtContentScheduleMeta['startDate'],
			endDate: `${gtgtContent.endDate}T${gtgtContent.endTime}:00.000Z`,
			contentType: gtgtContentScheduleMeta['contentType'],
			foregroundColor: gtgtContent.foregroundColor
		};

		if (arrivalDate !== undefined) {
			homrScheduleContent.arrivalDate = arrivalDate;
		}

		if (arrivalTime !== undefined) {
			homrScheduleContent.arrivalTime = arrivalTime;
		}

		return homrScheduleContent;
	}

	/**
	 * @description
	 * Gets the product promotion data and sets it on the component instance and emits an Event for other components
	 *
	 * @param catentryId
	 * @param channelId
	 */
	private getAndSetPromotionData$(
		catentryId: string | number,
		channelId: string,
		skuIdList: string[],
		ecode: string
	): void {
		// Get and set the promotions.  NOTE: ONLY DO THIS CLIENT-SIDE TO PREVENT PROMO FROM CACHING IN BASE PAGE

		const useNewPricingService = this.utilityService.checkKillSwitch(
			'useNewPricingService',
			this.killSwitches,
			chain.chainIdentifierAbbr
		);

		const skus = useNewPricingService ? skuIdList : undefined;

		this.discountEligibilityService
			.getItemLevelPromoDescription$(ecode, skus, catentryId, channelId, useNewPricingService)
			.pipe(take(1))
			.subscribe((promotions) => {
				if (this.isModernPromotionResponse(promotions)) {
					const promos = promotions?.itemPromoMessage
						? promotions.itemPromoMessage
						: promotions?.shippingPromoMessage;
					const promoData = {
						ProductLevelPromotion: {
							pdpDesc: promos.pdpItemMessage
								? promos.pdpItemMessage
								: promos.pdpShippingMessage,
							promotionId: promos.discountId,
							longDesc: '',
							promoDesc: promos.pdpItemMessage
								? promos.pdpItemMessage
								: promos.pdpShippingMessage,
							adminName: '',
							catentryId: '',
							groupName: '',
							priority: 0,
							promoFreeGift: ''
						}
					};
					this.promotionsMap = promoData;
				} else {
					this.promotionsMap = promotions;
				}
			})
			.add(() => {
				this.setPromoData();
			});
	}

	private isModernPromotionResponse(
		promoResponse: unknown
	): promoResponse is ModernPromotionResponse {
		const promoMaybe = promoResponse as ModernPromotionResponse;
		return Boolean(
			promoMaybe?.itemPromoMessage !== undefined ||
				promoMaybe?.shippingPromoMessage !== undefined
		);
	}

	setPromoData() {
		if (typeof this.promotionsMap === 'undefined' || this.promotionsMap === null) {
			this.promotionsMap = {};
		}

		// Check to see if the ComponentRef is defined and promotions input is not defined
		if (typeof this.dynamicComponentRef !== 'undefined') {
			// Set the promotions on the template reference
			this.dynamicComponentRef.instance['promotions'] = this.promotionsMap;

			// Start change detection
			this.dynamicComponentRef.changeDetectorRef.detectChanges();
		}

		// Trigger the PromoEvent Event
		this.ngRxstore.dispatch(setPromotionData({ promotionData: this.promotionsMap }));
	}

	/**
	 * @description
	 * calls to pointsService to populate store with userdata.
	 * points service updates store itself
	 */
	private getAndSetMyAccountData$() {
		return new Promise<void>((resolve, reject) => {
			if (this.cookieService.check('myLoyaltyInfo')) {
				const retrievedLoyaltyInfo = JSON.parse(this.cookieService.get('myLoyaltyInfo'));
				this.store.set('LOYALTY_BALANCE', retrievedLoyaltyInfo);
				this.eventBusService.emit(
					new EmitEvent(Events.LoyaltyBalance, retrievedLoyaltyInfo)
				);
				resolve();
			} else {
				this.pointsService
					.getPointSummary()
					.pipe(
						take(1),
						catchError((err) => {
							this.eventBusService.emit(new EmitEvent(Events.LoyaltyBalance, {}));
							return throwError(() => err.message);
						})
					)
					.subscribe({
						next: (loyaltyBalance) => {
							// setting cookie expiration to 30 mins from now.
							const expiration = new Date(new Date().getTime() + 1000 * 60 * 30);
							this.cookieService.set(
								'myLoyaltyInfo',
								JSON.stringify(loyaltyBalance),
								expiration,
								'/p'
							);
							this.eventBusService.emit(
								new EmitEvent(Events.LoyaltyBalance, loyaltyBalance)
							);
							resolve();
						},
						error: (err) => {
							return reject(new Error(err));
						}
					});
			}
		});
	}

	/**
	 * @description
	 * Loads a dynamic module (QV) by name
	 *
	 * @param componentId
	 */
	private loadDynamicModule$(
		componentId: string
	): Observable<{ didTemplateLoad: boolean; templateName: string }> {
		return new Observable<{ didTemplateLoad: boolean; templateName: string }>((observer) => {
			const importModule = (
				moduleItem: ImportedModule<DynamicProductTemplateModule>,
				component: string
			) => {
				// Pass the imported component module in to create the component instance
				this.createDynamicComponent$(moduleItem[component])
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							// Stream true
							observer.next({
								didTemplateLoad: true,
								templateName: moduleItem[component].name
							});

							// Complete the stream
							observer.complete();
						},
						error: (error) => {
							observer.error(error);
						}
					});
			};

			switch (componentId) {
				case DynamicProductTemplateModules.IN_STOCK_DEFAULT:
					import(
						/* webpackChunkName: "in-stock-default" */ '@pdp/templates/in-stock-default/in-stock-default.module'
					)
						.then((moduleItem) => {
							importModule(moduleItem, 'InStockDefaultModule');
						})
						.catch((err) => {
							this.logger.error(`Error loading InStockDefaultModule: ${err}`);
						});
					break;

				case DynamicProductTemplateModules.OUT_OF_STOCK_DEFAULT:
					import(
						/* webpackChunkName: "out-of-stock-default" */ '@pdp/templates/out-of-stock-default/out-of-stock-default.module'
					)
						.then((moduleItem) => {
							importModule(moduleItem, 'OutOfStockDefaultModule');
						})
						.catch((err) => {
							this.logger.error(`Error loading OutOfStockDefaultModule: ${err}`);
						});
					break;

				case DynamicProductTemplateModules.CUSTOM_DEFAULT:
					import(
						/* webpackChunkName: "custom-default" */ '@pdp/templates/custom-default/custom-default.module'
					)
						.then((moduleItem) => {
							importModule(moduleItem, 'CustomDefaultModule');
						})
						.catch((err) => {
							this.logger.error(`Error loading CustomDefaultModule: ${err}`);
						});
					break;

				case DynamicProductTemplateModules.BUNDLE_DEFAULT:
					import(
						/* webpackChunkName: "bundle-default" */ '@pdp/templates/bundle-default/bundle-default.module'
					)
						.then((moduleItem) => {
							importModule(moduleItem, 'BundleDefaultModule');
						})
						.catch((err) => {
							this.logger.error(`Error loading BundleDefaultModule: ${err}`);
						});
					break;

				case DynamicProductTemplateModules.NOT_FOUND_DEFAULT:
					import(
						/* webpackChunkName: "not-found" */ '@pdp/templates/not-found/not-found.module'
					)
						.then((moduleItem) => {
							importModule(moduleItem, 'NotFoundModule');
						})
						.catch((err) => {
							this.logger.error(`Error loading NotFoundModule: ${err}`);
						});
					break;

				default:
					observer.error(
						new Error(
							`Unknown component ID: ${componentId}. Unable to render any lazy components.`
						)
					);
					break;
			}
		});
	}

	/**
	 * @description
	 * Loads any data not required before the template is loaded, and is able to set template data
	 * because the dynamicComponentRef instance is defined
	 *
	 * @param product
	 */
	private loadTemplatePostLoadData(product: ProcessedProduct, channelId: string): void {
		if (this.isPlatformBrowser) {
			/*
			 * Promotions
			 */
			// Get and set the promotion data
			this.getAndSetPromotionData$(
				product.catentryId,
				channelId,
				product.skuIdList,
				product.partNumber
			);

			/*
			 * homR Content
			 */
			// call homR to get content for page
			if (product.partNumber) {
				this.ngRxstore
					.select(selectEcommInventory)
					.pipe(take(2))
					.subscribe((inventory) => {
						if (inventory) {
							this.shipToMeInventoryMap = inventory;
							this.getAndSetHomrData$();
						}
					});
			}

			// Get the manufacturer i.e X_BRAND
			const manufacturer =
				'descriptiveAttributes' in product
					? product.descriptiveAttributes?.find((attr) => attr.identifier === 'X_BRAND')
					: undefined;

			/*
			 * ScoreCard Eligibility
			 */
			if (
				typeof manufacturer !== 'undefined' &&
				typeof manufacturer.value === 'string' &&
				manufacturer.value.toLowerCase().trim().indexOf('ping') === -1 &&
				manufacturer.value.toLowerCase().trim().indexOf('hoka one one') === -1 &&
				this.utilityService.checkKillSwitch(
					'ScoreCardData',
					this.killSwitches,
					chain.chainIdentifierAbbr
				)
			) {
				this.loyaltyService.getScorecardSkuEligibility(product).pipe(take(1)).subscribe();
			}

			/*
			 * myAccount Content for registered user
			 */
			if (
				this.utilityService.checkKillSwitch(
					'registeredUsersEnabled',
					this.killSwitches,
					chain.chainIdentifierAbbr
				)
			) {
				this.authService
					.getIsAuthenticated$()
					.pipe(
						take(1),
						catchError((err) => {
							// emit event here for failed or error. to ensure page load data set
							this.eventBusService.emit(new EmitEvent(Events.LoyaltyBalance, {}));
							// Return false because there was an error validating authentication status
							return throwError(err);
						})
					)
					.subscribe((authenticatedResponse) => {
						if (authenticatedResponse === false) {
							this.eventBusService.emit(new EmitEvent(Events.LoyaltyBalance, {}));
						} else {
							this.getAndSetMyAccountData$();
						}
					});
			} else {
				// emit event here for failed or error. to ensure page load data set
				this.eventBusService.emit(new EmitEvent(Events.LoyaltyBalance, {}));
			}

			/*
			 * Load products's pros and cons
			 */
			if (product.partNumber && this.isHighlyRatedReviewsEnabled()) {
				this.reviewService
					.getProsConsByProductId$(product.partNumber)
					.pipe(take(1), takeUntil(this.destroy$))
					.subscribe((resp: ProductReviewsProsCons) => {
						this.prosConsStore.dispatch(setProductReviewProsCons(resp));
					});
			}
		}
	}

	/**
	 * @description
	 * Creates a dynamic component for a given component module
	 *
	 * @param componentModuleRef
	 */
	private createDynamicComponent$(
		componentModuleRef: Type<DynamicProductTemplateModule>
	): Observable<boolean> {
		return from(this.createDynamicComponent(componentModuleRef)).pipe(
			switchMap(() =>
				// Stream true
				of(true)
			),
			catchError((error) => {
				console.error(
					`${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.client}ERROR CREATING DYNAMIC COMPONENT: ${error.message}`
				);
				return of(false);
			})
		);
	}

	private createDynamicComponent(
		componentModuleRef: Type<DynamicProductTemplateModule>
	): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			try {
				// Create the injector
				const injector = createInjector(componentModuleRef, this.injector);

				// Get the component module from the injector
				const componentModule =
					injector.get<DynamicProductTemplateModule>(componentModuleRef);

				// Resolve the component factory
				const componentFactory: ComponentFactory<DynamicProductTemplateComponent> =
					componentModule.resolveComponentFactory();

				// Create the template component and get its reference
				this.dynamicComponentRef = this.templateOutlet.createComponent(componentFactory);

				// Set properties on the dynamic template
				const instance = this.dynamicComponentRef.instance;
				if (instance.templateName !== DynamicProductTemplateModules.NOT_FOUND_DEFAULT) {
					instance.product = this.product as ProcessedProduct;
					instance.killSwitches = this.killSwitches;
					instance.sourceAboveTheFoldViewRef = this.aboveTheFoldViewRef;
					if (
						instance.templateName === DynamicProductTemplateModules.IN_STOCK_DEFAULT ||
						instance.templateName === DynamicProductTemplateModules.CUSTOM_DEFAULT
					) {
						instance.shipToMeInventoryMap = this.shipToMeInventoryMap;
						instance.bopisLocationInventoryMap = this.bopisLocationInventoryMap;
					}
					if (instance.templateName === DynamicProductTemplateModules.CUSTOM_DEFAULT) {
						instance.myCustomizerMap = this.myCustomizerMap;
					}
				}

				// Start change detection
				this.dynamicComponentRef.changeDetectorRef.detectChanges();

				resolve();
			} catch (error) {
				reject(
					new Error(
						`${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.client}ERROR CREATING DYNAMIC COMPONENT: ${error.message}`
					)
				);
			}
		});
	}

	setProductPageSEOData(eCode: string, title: string, seoData: ProductStyleSeo) {
		// Ensure type safety access for below properties
		this.page = seoData || {};

		// Get the page title
		const pageTitle = (
			this.page.title || this.seoService.getDefaultPageTitle(title, chain.chainName)
		)
			.replace(/\s+/g, ' ')
			.trim();

		// Get the trimmed page title
		const pageTitleTrimmed = this.seoService.getTrimmedPageTitle(pageTitle);

		// Get the meta description
		const metaDescription = (
			this.page.metaDescription ||
			seoData?.metaDesc ||
			this.seoService.getDefaultPageMetaDescription(title, chain.chainIdentifierAbbr)
		)
			.replace(/\s+/g, ' ')
			.trim();

		// Get the meta keywords
		const metaKeywords = (this.page.metaKeyword || '').trim();

		/*
		 * Set the SEO tags regardless if success or failure of page API
		 */
		// Set page title
		this.title.setTitle(pageTitle);

		// Check the current runtime context
		if (!this.isPlatformBrowser) {
			// Set the meta tags only if current context is server
			this.metaService.addTags(
				[
					{ name: 'apple-mobile-web-app-title', content: pageTitle },
					{ name: 'description', content: metaDescription },
					{ name: 'keywords', content: metaKeywords },
					{ property: 'og:type', content: 'product' },
					{ property: 'og:site_name', content: chain.chainName },
					{ property: 'og:title', content: pageTitleTrimmed },
					{ property: 'og:description', content: metaDescription },
					{
						property: 'og:image',
						content: this.scene7Service.getProductImageUrl(
							eCode,
							'ProductBean',
							this.defaultColor,
							undefined,
							{ wid: '200' }
						)
					}
				],
				true
			);
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.utilityService.setupQuestionsAndAnswersAIWidget('');
	}
}
